import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface TokenPayload {
  meeting_id: string;
  meeting_code: string;
  user_name: string;
  role: "host" | "participant";
  app_id: string;
  exp: number;
}

interface AppBranding {
  name: string;
  logo_url: string | null;
  theme_color: string | null;
}

export default function EmbedMeeting() {
  const { meetingCode } = useParams<{ meetingCode: string }>();
  const [searchParams] = useSearchParams();
  const tokenParam = searchParams.get("token");
  const nameParam = searchParams.get("name");

  const [phase, setPhase] = useState<"verifying" | "name-prompt" | "lobby" | "in-call" | "ended" | "error">(
    "verifying",
  );
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [payload, setPayload] = useState<TokenPayload | null>(null);
  const [meetingId, setMeetingId] = useState<string>("");
  const [userName, setUserName] = useState<string>(nameParam || "");
  const [branding, setBranding] = useState<AppBranding | null>(null);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const localVideoRef = useRef<HTMLVideoElement>(null);

  // 1. Verify token (or look up public meeting if no token)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!meetingCode) {
        setErrorMsg("Missing meeting code");
        setPhase("error");
        return;
      }
      try {
        if (tokenParam) {
          const { data, error } = await supabase.functions.invoke("v1-api/verify-token", {
            body: { token: tokenParam },
          });
          if (error || !data?.valid) throw new Error(data?.error || "Invalid or expired token");
          if (cancelled) return;
          const p = data.payload as TokenPayload;
          if (p.meeting_code !== meetingCode) throw new Error("Token does not match this meeting");
          setPayload(p);
          setMeetingId(p.meeting_id);
          setUserName(p.user_name);
          await loadBranding(p.app_id);
          setPhase("lobby");
        } else {
          // Public access via meeting code (no token)
          const { data: meetings, error } = await supabase.rpc("get_meeting_by_code", {
            _meeting_code: meetingCode,
          });
          if (error) throw error;
          const m = Array.isArray(meetings) ? meetings[0] : null;
          if (!m) throw new Error("Meeting not found or not active");
          if (cancelled) return;
          setMeetingId(m.id);
          if (!nameParam) setPhase("name-prompt");
          else setPhase("lobby");
        }
      } catch (e) {
        if (!cancelled) {
          setErrorMsg((e as Error).message);
          setPhase("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingCode, tokenParam]);

  async function loadBranding(appId: string) {
    const { data } = await supabase
      .from("apps")
      .select("name, logo_url, theme_color")
      .eq("id", appId)
      .maybeSingle();
    if (data) setBranding(data as AppBranding);
  }

  // 2. Acquire media when entering lobby
  useEffect(() => {
    if (phase !== "lobby" && phase !== "in-call") return;
    let active = true;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        if (!active) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        setStream(s);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = s;
        }
      } catch (e) {
        toast.error("Could not access camera/microphone");
        console.error(e);
      }
    })();
    return () => {
      active = false;
    };
  }, [phase]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const toggleAudio = useCallback(() => {
    if (!stream) return;
    stream.getAudioTracks().forEach((t) => (t.enabled = !audioOn));
    setAudioOn(!audioOn);
  }, [stream, audioOn]);

  const toggleVideo = useCallback(() => {
    if (!stream) return;
    stream.getVideoTracks().forEach((t) => (t.enabled = !videoOn));
    setVideoOn(!videoOn);
  }, [stream, videoOn]);

  const joinCall = async () => {
    if (!userName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!meetingId) return;
    try {
      // Register as guest participant (allowed by RLS for active meetings)
      await supabase.from("meeting_participants").insert({
        meeting_id: meetingId,
        user_id: "00000000-0000-0000-0000-000000000000", // guest sentinel
        guest_name: userName.trim(),
        is_audio_enabled: audioOn,
        is_video_enabled: videoOn,
        role: payload?.role || "participant",
      });
    } catch (e) {
      console.warn("Participant insert failed:", e);
    }
    setPhase("in-call");
  };

  const leave = () => {
    stream?.getTracks().forEach((t) => t.stop());
    setStream(null);
    setPhase("ended");
  };

  const themeColor = branding?.theme_color || "#6366f1";

  // --- Renders ---

  if (phase === "verifying") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold mb-2">Cannot join meeting</h1>
          <p className="text-muted-foreground text-sm">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (phase === "ended") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold mb-2">You left the meeting</h1>
          <p className="text-muted-foreground text-sm">Thanks for joining{branding?.name ? ` on ${branding.name}` : ""}.</p>
        </div>
      </div>
    );
  }

  if (phase === "name-prompt") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-4">
          {branding?.logo_url && <img src={branding.logo_url} alt={branding.name} className="h-10 mx-auto" />}
          <h1 className="text-lg font-semibold text-center">Join meeting</h1>
          <Input
            placeholder="Your name"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && userName.trim() && setPhase("lobby")}
            autoFocus
          />
          <Button
            className="w-full"
            style={{ backgroundColor: themeColor }}
            disabled={!userName.trim()}
            onClick={() => setPhase("lobby")}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top bar */}
      <header className="flex items-center justify-between p-3 border-b">
        <div className="flex items-center gap-2">
          {branding?.logo_url && <img src={branding.logo_url} alt={branding.name} className="h-6" />}
          <span className="text-sm font-medium">{branding?.name || "Meeting"}</span>
        </div>
        <span className="text-xs text-muted-foreground">{userName}</span>
      </header>

      {/* Video */}
      <main className="flex-1 flex items-center justify-center bg-muted/30 p-4">
        <div className="relative w-full max-w-3xl aspect-video bg-black rounded-lg overflow-hidden">
          {videoOn ? (
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/60">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-semibold"
                style={{ backgroundColor: themeColor }}
              >
                {userName.charAt(0).toUpperCase() || "?"}
              </div>
            </div>
          )}
          <div className="absolute bottom-2 left-3 text-xs text-white bg-black/40 px-2 py-1 rounded">
            {userName} {payload?.role === "host" && "(host)"}
          </div>
        </div>
      </main>

      {/* Controls */}
      <footer className="flex items-center justify-center gap-3 p-4 border-t">
        <Button
          size="icon"
          variant={audioOn ? "secondary" : "destructive"}
          onClick={toggleAudio}
          aria-label="Toggle microphone"
        >
          {audioOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
        </Button>
        <Button
          size="icon"
          variant={videoOn ? "secondary" : "destructive"}
          onClick={toggleVideo}
          aria-label="Toggle camera"
        >
          {videoOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
        </Button>
        {phase === "lobby" ? (
          <Button style={{ backgroundColor: themeColor }} onClick={joinCall}>
            Join now
          </Button>
        ) : (
          <Button variant="destructive" onClick={leave}>
            <PhoneOff className="h-4 w-4 mr-2" />
            Leave
          </Button>
        )}
      </footer>
    </div>
  );
}
